import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import TextSelectionMenu from "components/TextSelectionMenu/TextSelectionMenu";
import bookmarkFilledImg from "images/bookmark-filled.svg";
import bookmarkImg from "images/bookmark.svg";
import {
  DictionarySearchResponse,
  addSavedWordMutation,
  deleteSavedWordMutation,
  personalDictionaryQuery,
} from "services/dictionary";
import { logError } from "services/logging";
import DictionaryItem from "./DictionaryItem";
import "../tabs.css";

type DefineTabProps = {
  defineContent: DictionarySearchResponse | string | null;
  isSelectionOpened: boolean;
};

export default function DefineTab({ defineContent, isSelectionOpened }: DefineTabProps) {
  const queryClient = useQueryClient();

  // Query the saved list scoped to the displayed word, to tell if it's bookmarked.
  const lookupWord =
    typeof defineContent === "object" && defineContent !== null
      ? defineContent.word
      : undefined;
  const savedWordsOptions = personalDictionaryQuery({ filter: lookupWord });
  const savedWordsQuery = useQuery({
    ...savedWordsOptions,
    queryFn: lookupWord ? savedWordsOptions.queryFn : skipToken,
  });
  const savedWords = savedWordsQuery.data?.list ?? [];

  const addWord = useMutation(addSavedWordMutation());
  const deleteWord = useMutation(deleteSavedWordMutation());

  const invalidateSavedWords = () => {
    queryClient.invalidateQueries({ queryKey: personalDictionaryQuery.queryKeyPrefix });
  };

  const onToggleWord = (content: DictionarySearchResponse) => {
    const item = savedWords.find(({ word }) => word === content.word);
    const options = {
      onSuccess: invalidateSavedWords,
      onError: logError,
    };
    if (item) {
      deleteWord.mutate({ id: item.id }, options);
    } else {
      addWord.mutate({ word: content.word }, options);
    }
  };

  const selectionBehaviour = (
    <TextSelectionMenu
      isContextMenuOpened={isSelectionOpened}
      scrollableParentId="dictionary-items"
      positionalParentId="sidebar-id"
      selectionContext="sidebar"
    />
  );

  if (defineContent === null) {
    return null;
  }

  if (typeof defineContent === "string") {
    return (
      <div className="dictionary-items" id="dictionary-items">
        <span>{defineContent}</span>
        {selectionBehaviour}
      </div>
    );
  }

  return (
    <div className="dictionary-items" id="dictionary-items">
      <div className="dictionary-word-wrapper">
        <span className="dictionary-word">{defineContent.word}</span>
        <div className="dictionary-action" onClick={() => onToggleWord(defineContent)}>
          <img
            src={
              savedWords.find(({ word }) => word === defineContent.word)
                ? bookmarkFilledImg
                : bookmarkImg
            }
            alt="addIcon"
          />
        </div>
      </div>
      {defineContent.pos &&
        Object.entries(defineContent.pos).map(([pos, entries], index) => (
          <DictionaryItem key={index} pos={pos} items={entries} />
        ))}
      {selectionBehaviour}
    </div>
  );
}
